import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import ImageWithFallback from '../../components/ImageWithFallback';
import ImageCarousel from '../../components/ImageCarousel';
import GlassCapsulePopup from '../../components/GlassCapsulePopup';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { updateSEO, generateListingSEO, resetSEO } from '../../utils/seo';
import './Listing.css';
import ZoomableImage from '../../components/ZoomableImage/ZoomableImage';
import SignInPopup from '../../components/SignInPopup';
import BoostModal from '../../components/BoostModal/BoostModal';
import instagramIcon from '../../images/Instagram_icon.png';


interface ListingType {
    id: string;
    title: string;
    description: string;
    unit_type: string;
    address: string;
    latitude?: number | null;
    longitude?: number | null;
    monthly_rent: number;
    pictures: string[];
    compressed_pictures?: string[];
    lease_term: string;
    lease_start_date?: string;
    lease_end_date?: string;
    university_id: number;
    university_name?: string;
    university_abbreviation?: string;
    user_id?: string;
    parking?: string | boolean;
    allow_pets?: string | boolean;
    gym?: string | boolean;
    pool?: string | boolean;
    laundry?: string | boolean;
    gender_preference?: string;
    verification_status?: string;
    source?: string;
    facebook_url?: string;
    facebook_first_name?: string;
    facebook_last_name?: string;
    facebook_avatar_url?: string;
    instagram_username?: string;
    created_at?: string;
    // Alt-listing fields
    is_alt_listing?: boolean;
    facebook_person_name?: string;
    facebook_profile_picture_url?: string;
    // Boost fields
    is_boosted?: boolean;
    boost_expiration?: string;
    city?: string;
}

interface User {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    university?: string;
    university_id?: number;
    created_at: string;
    is_verified: boolean;
    is_google_user: boolean;
    avatar_url?: string;
    instagram?: string;
    student_verification_status?: 'unverified' | 'pending' | 'approved' | 'rejected';
}

const Listing = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, user } = useAuth();
    const [listing, setListing] = useState<ListingType | null>(null);
    const [owner, setOwner] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [messageError, setMessageError] = useState<string>('');
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [viewFull, setViewFull] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [mobilePhotoIndex, setMobilePhotoIndex] = useState(0);
    const [desktopPhotoIndex, setDesktopPhotoIndex] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [showLockedPopup, setShowLockedPopup] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [isSignInPopupOpen, setIsSignInPopupOpen] = useState(false);
    const thumbnailsRef = useRef<HTMLDivElement | null>(null);
    const [isPhotoHovered, setIsPhotoHovered] = useState(false);
    const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
    const [showBoostSuccess, setShowBoostSuccess] = useState(false);
    const [showTimeRemaining, setShowTimeRemaining] = useState(false);

    // Track which images have loaded their full versions
    const [loadedFullImages, setLoadedFullImages] = useState<Set<number>>(new Set());

    // Start with compressed images if available, then switch to full when loaded
    const getImageUrl = (index: number, preferCompressed: boolean = false) => {
        if (!listing?.pictures || !listing.pictures[index]) return undefined;

        try {
            // If full image has loaded, use it
            if (loadedFullImages.has(index)) {
                const pic = listing.pictures[index];
                if (pic) {
                    return pic.startsWith('http') ? pic : `${API_BASE_URL || ''}${pic}`;
                }
            }

            // Otherwise, use compressed if available
            if (listing.compressed_pictures &&
                listing.compressed_pictures.length > index &&
                listing.compressed_pictures[index]) {
                const compressedPic = listing.compressed_pictures[index];
                if (compressedPic) {
                    return compressedPic.startsWith('http') ? compressedPic : `${API_BASE_URL || ''}${compressedPic}`;
                }
            }

            // Fallback to full image
            const pic = listing.pictures[index];
            if (pic) {
                return pic.startsWith('http') ? pic : `${API_BASE_URL || ''}${pic}`;
            }
        } catch (error) {
            console.error('Error getting image URL:', error);
        }

        return undefined;
    };

    // Get the full image URL for preloading
    const getFullImageUrl = (index: number) => {
        if (!listing?.pictures || !listing.pictures[index]) return undefined;
        try {
            const pic = listing.pictures[index];
            if (pic) {
                return pic.startsWith('http') ? pic : `${API_BASE_URL || ''}${pic}`;
            }
        } catch (error) {
            console.error('Error getting full image URL:', error);
        }
        return undefined;
    };

    // Preload full images in the background, prioritizing first image
    useEffect(() => {
        if (!listing || !listing.pictures || !Array.isArray(listing.pictures) || listing.pictures.length === 0) {
            return;
        }

        try {
            // Reset loaded images when listing changes
            setLoadedFullImages(new Set());

            // Immediately preload first image (highest priority)
            const firstPic = listing.pictures[0];
            if (firstPic && typeof firstPic === 'string') {
                const firstFullUrl = firstPic.startsWith('http')
                    ? firstPic
                    : `${API_BASE_URL || ''}${firstPic}`;
                if (firstFullUrl) {
                    const img = new Image();
                    img.onload = () => {
                        setLoadedFullImages(prev => new Set(prev).add(0));
                    };
                    img.onerror = () => {
                        // Silently handle errors
                    };
                    img.src = firstFullUrl;
                }
            }

            // Preload remaining images after a short delay to prioritize the first one
            const preloadTimer = setTimeout(() => {
                for (let index = 1; index < listing.pictures.length; index++) {
                    const pic = listing.pictures[index];
                    if (pic && typeof pic === 'string') {
                        const fullImageUrl = pic.startsWith('http')
                            ? pic
                            : `${API_BASE_URL || ''}${pic}`;
                        if (fullImageUrl) {
                            const img = new Image();
                            img.onload = () => {
                                setLoadedFullImages(prev => new Set(prev).add(index));
                            };
                            img.onerror = () => {
                                // Silently handle errors
                            };
                            img.src = fullImageUrl;
                        }
                    }
                }
            }, 500); // 500ms delay for secondary preloading

            return () => clearTimeout(preloadTimer);
        } catch (error) {
            console.error('Error preloading images:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listing?.id]);

    const scrollThumbnails = (direction: 'left' | 'right') => {
        const el = thumbnailsRef.current;
        if (!el) return;
        const scrollAmount = Math.max(el.clientWidth * 0.8, 240);
        el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    };



    useEffect(() => {
        // Scroll to top when component mounts or id changes
        window.scrollTo(0, 0);

        const fetchListing = async () => {
            if (!id) {
                setLoading(false);
                return;
            }

            try {
                // Try fetching regular listing first
                let response = await fetch(API_ENDPOINTS.SUBLEASES.DETAIL(id));
                let listingData = null;

                if (response.ok) {
                    listingData = await response.json();
                } else {
                    // If regular listing not found, try alt-listing
                    response = await fetch(API_ENDPOINTS.ALT_LISTINGS.DETAIL(id));
                    if (response.ok) {
                        listingData = await response.json();
                        listingData.is_alt_listing = true;
                    }
                }

                if (listingData) {
                    setListing(listingData);

                    // Fetch owner information if user_id exists (not for alt-listings)
                    if (listingData.user_id && !listingData.is_alt_listing) {
                        try {
                            const ownerResponse = await fetch(API_ENDPOINTS.USERS.GET_USER(listingData.user_id));
                            if (ownerResponse.ok) {
                                const ownerData = await ownerResponse.json();
                                setOwner(ownerData);
                            }
                        } catch (error) {
                            console.error('Error fetching owner:', error);
                        }
                    }

                    // Update SEO for this listing
                    const seoData = generateListingSEO(listingData);
                    updateSEO(seoData);
                } else {
                    console.error('Failed to fetch listing');
                }
            } catch (error) {
                console.error('Error fetching listing:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchListing();

        // Cleanup: reset SEO when component unmounts
        return () => {
            resetSEO();
        };
    }, [id]);

    const isListingFeatured = useCallback(() => {
        if (!listing) return false;
        if (listing.is_boosted !== true && String(listing.is_boosted).toLowerCase() !== 'true') return false;
        if (!listing.boost_expiration) return true;
        try {
            const exp = new Date(listing.boost_expiration);
            return exp > new Date();
        } catch {
            return true;
        }
    }, [listing]);

    useEffect(() => {
        if (searchParams.get('boost_success') === 'true' && listing && !loading) {
            setShowBoostSuccess(true);
            
            // Clear URL params
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('boost_success');
            newParams.delete('listing_id');
            const cleanUrl = `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
            window.history.replaceState({}, '', cleanUrl);

            // Show time remaining popup automatically after a delay
            setTimeout(() => {
                setShowBoostSuccess(false);
                setShowTimeRemaining(true);
            }, 3000);
        }
    }, [searchParams, listing, loading]);

    useEffect(() => {
        if (searchParams.get('boost_post') !== '1' || !listing || loading) return;
        if (!user?.id || listing.user_id !== user.id) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('boost_post');
            const q = newParams.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
            return;
        }
        setIsBoostModalOpen(true);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('boost_post');
        const q = newParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
    }, [searchParams, listing, loading, user?.id]);

    const formatTimeRemaining = (expiration: string): string => {
        const now = new Date();
        const exp = new Date(expiration);
        const diff = exp.getTime() - now.getTime();

        if (diff <= 0) return 'Expired';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (days >= 1) return `${days} day${days === 1 ? '' : 's'} left`;
        if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} left`;
        return `${Math.max(mins, 1)} minute${mins === 1 ? '' : 's'} left`;
    };

    const formatTerm = (term: string) => {
        const termMap: { [key: string]: string } = {
            'fall': 'Fall',
            'spring': 'Spring',
            'summer': 'Summer'
        };

        // Handle comma-separated terms (e.g., "spring,summer" or "spring, summer")
        if (term.includes(',')) {
            return term
                .split(',')
                .map(t => {
                    const trimmed = t.trim().toLowerCase();
                    return termMap[trimmed] || trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
                })
                .join(', ');
        }

        // Handle single term
        const lowerTerm = term.toLowerCase();
        return termMap[lowerTerm] || term;
    };

    const formatUnitType = (unitType: string) => {
        // Convert unit types like "4x4" to "4 Bedroom, 4 Bathroom"
        const parts = unitType.split('x');
        if (parts.length === 2) {
            const bedrooms = parts[0];
            const bathrooms = parts[1];
            return `${bedrooms} Bedroom${bedrooms !== '1' ? 's' : ''}, ${bathrooms} Bathroom${bathrooms !== '1' ? 's' : ''}`;
        }
        return unitType;
    };

    const formatLeaseDates = (listing: ListingType) => {
        let start = listing.lease_start_date;
        let end = listing.lease_end_date;
        
        // If missing, infer from term (fallback for UI while DB is updating)
        if (!start || !end) {
            const term = (listing.lease_term || '').toLowerCase();
            if (term.includes('spring')) {
                start = start || '2026-01-01';
                end = end || '2026-05-31';
            } else if (term.includes('summer')) {
                start = start || '2026-05-01';
                end = end || '2026-07-31';

            } else if (term.includes('fall')) {
                start = start || '2026-08-01';
                end = end || '2026-12-31';
            }
        }
        
        if (start && end) {
            const s = new Date(start + 'T00:00:00');
            const e = new Date(end + 'T00:00:00');
            
            // Check if we are on a small screen to abbreviate dates
            const isSmallScreen = window.innerWidth <= 768;
            
            if (isSmallScreen) {
                const shortOptions: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric' };
                return `${s.toLocaleDateString('en-US', shortOptions)} - ${e.toLocaleDateString('en-US', shortOptions)}`;
            }

            const formatOptions: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
            return `${s.toLocaleDateString('en-US', formatOptions)} - ${e.toLocaleDateString('en-US', formatOptions)}`;
        }
        
        return null;
    };

    const openGallery = (index: number) => {
        setCurrentImageIndex(index);
        setGalleryOpen(true);
        // Prevent background scrolling when gallery is open
        document.body.style.overflow = 'hidden';
    };

    const closeGallery = () => {
        setGalleryOpen(false);
        // Restore background scrolling when gallery is closed
        document.body.style.overflow = '';
    };

    const openViewFull = () => {
        setViewFull(true);
        // Prevent background scrolling when viewFull is open
        document.body.style.overflow = 'hidden';
    };

    const closeViewFull = () => {
        setViewFull(false);
        // Restore background scrolling when viewFull is closed
        document.body.style.overflow = '';
    };

    const nextViewFullImage = () => {
        if (!listing?.pictures) return;
        setDesktopPhotoIndex((prev) => (prev + 1) % listing.pictures.length);
    };

    const prevViewFullImage = () => {
        if (!listing?.pictures) return;
        setDesktopPhotoIndex((prev) => (prev - 1 + listing.pictures.length) % listing.pictures.length);
    };

    const nextImage = () => {
        if (!listing?.pictures) return;
        setCurrentImageIndex((prev) => (prev + 1) % listing.pictures.length);
    };

    const prevImage = () => {
        if (!listing?.pictures) return;
        setCurrentImageIndex((prev) => (prev - 1 + listing.pictures.length) % listing.pictures.length);
    };


    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (viewFull) {
            switch (event.key) {
                case 'Escape':
                    closeViewFull();
                    break;
                case 'ArrowLeft':
                    prevViewFullImage();
                    break;
                case 'ArrowRight':
                    nextViewFullImage();
                    break;
            }
            return;
        }

        if (!galleryOpen) return;

        switch (event.key) {
            case 'Escape':
                closeGallery();
                break;
            case 'ArrowLeft':
                prevImage();
                break;
            case 'ArrowRight':
                nextImage();
                break;
        }
    }, [galleryOpen, viewFull]);

    useEffect(() => {
        // Keep the active thumbnail in view when desktop photo changes
        const container = thumbnailsRef.current;
        if (!container) return;
        const active = container.querySelector(`[data-thumb-index="${desktopPhotoIndex}"]`) as HTMLElement | null;
        active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, [desktopPhotoIndex]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [galleryOpen, viewFull, handleKeyDown]);

    // Cleanup: restore scrolling when component unmounts or overlays close
    useEffect(() => {
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleSendMessage = async () => {
        if (!isAuthenticated) {
            setMessageError('Please log in to send messages');
            return;
        }

        if (!listing || !listing.user_id) {
            setMessageError('Unable to contact listing owner');
            return;
        }

        if (user?.id === listing.user_id) {
            setMessageError('You cannot message yourself');
            return;
        }

        try {
            setMessageError('');
            const token = localStorage.getItem('auth_token');

            if (!token) {
                setMessageError('Authentication token not found. Please log in again.');
                return;
            }

            // First, try to get existing conversation with this user
            // No need to assign variable; navigate immediately when known

            try {
                const existingConversationResponse = await axios.get(
                    API_ENDPOINTS.MESSAGES.WITH_USER(listing.user_id),
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                // existing conversation found; proceed to navigate
            } catch (error: any) {
                // If conversation doesn't exist, create a new one
                if (error.response?.status === 404) {
                    await axios.post(
                        API_ENDPOINTS.MESSAGES.CREATE_CONVERSATION,
                        {
                            participant_id: listing.user_id,
                            listing_id: listing.id
                        },
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                } else {
                    throw error;
                }
            }

            // Navigate to messages page with user + listing to auto-select correct conversation
            navigate(`/messages?userId=${listing.user_id}&listingId=${listing.id}`);

        } catch (error: any) {
            console.error('Error creating conversation:', error);
            setMessageError(error.response?.data?.detail || 'Failed to start conversation');
        }
    };

    const handleTooltipClick = (tooltipId: string) => {
        setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId);
    };

    const handleShare = async () => {
        try {
            const listingUrl = `${window.location.origin}/listing/${id}`;
            await navigator.clipboard.writeText(listingUrl);
            setShowSharePopup(true);
            setTimeout(() => setShowSharePopup(false), 2000);
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    };

    if (loading) {
        return (
            <>
                <NavBar inverted={true} />
                <div className="listing-page">
                    <div className="listing-page-skeleton">
                        {/* Title */}
                        <div className="skeleton-block skeleton-title"></div>
                        {/* Rent capsule row */}
                        <div className="skeleton-capsule-row">
                            <div className="skeleton-capsule"></div>
                            <div className="skeleton-capsule skeleton-capsule-wide"></div>
                            <div className="skeleton-capsule"></div>
                        </div>
                        {/* Photo */}
                        <div className="skeleton-photo"></div>
                        {/* Thumbnail strip */}
                        <div className="skeleton-thumbnails">
                            <div className="skeleton-thumb"></div>
                            <div className="skeleton-thumb"></div>
                            <div className="skeleton-thumb"></div>
                            <div className="skeleton-thumb"></div>
                            <div className="skeleton-thumb"></div>
                        </div>
                        {/* Description */}
                        <div className="skeleton-card">
                            <div className="skeleton-line skeleton-line-short"></div>
                            <div className="skeleton-line"></div>
                            <div className="skeleton-line"></div>
                            <div className="skeleton-line skeleton-line-medium"></div>
                        </div>
                        {/* Details row */}
                        <div className="skeleton-details-row">
                            <div className="skeleton-card skeleton-card-half">
                                <div className="skeleton-line skeleton-line-short"></div>
                                <div className="skeleton-line"></div>
                                <div className="skeleton-line"></div>
                                <div className="skeleton-line skeleton-line-medium"></div>
                            </div>
                            <div className="skeleton-card skeleton-card-half">
                                <div className="skeleton-line skeleton-line-short"></div>
                                <div className="skeleton-line skeleton-line-medium"></div>
                                <div className="skeleton-line"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (!listing) {
        return (
            <div className="listing-page">
                <div className="not-found">Listing not found.</div>
            </div>
        );
    }

    return (
        <>
            <NavBar inverted={true} />
            <div className="listing-page">
                <div className="listing-container">


                    {/* Title */}
                    <div className="listing-title-section">
                        <h1 className="listing-page-title">
                            <span className="title-text">{listing.title}</span>
                        </h1>
                    </div>

                    {/* Monthly Rent Capsule (replaces listing ID text) */}
                    <div className="rent-capsule-row">
                        <div className="rent-capsule" aria-label="Monthly rent">
                            <span className="rent-capsule-amount">${listing.monthly_rent}</span>
                            <span className="rent-capsule-suffix">/mo</span>
                        </div>
                        {formatLeaseDates(listing) && (
                            <div className="lease-dates-capsule">
                                {formatLeaseDates(listing)}
                            </div>
                        )}
                        <button className="share-capsule" onClick={handleShare} aria-label="Share listing">
                            Share
                        </button>
                    </div>




                    {/* Photos layout */}
                    <div className="photos-container">
                        {/* Desktop layout - single photo with navigation */}
                        <div className="desktop-photos">
                            <div 
                                className={`desktop-photo-container ${isPhotoHovered ? 'photo-hovered' : ''}`} 
                                onClick={openViewFull} 
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setIsPhotoHovered(true)}
                                onMouseLeave={() => setIsPhotoHovered(false)}
                            >
                                {/* Layered image display: compressed base + HD overlay */}
                                <div className="image-layer-container">
                                    {/* Compressed image as base layer */}
                                    <ImageWithFallback
                                        key={`desktop-compressed-${desktopPhotoIndex}`}
                                        src={(() => {
                                            if (!listing?.compressed_pictures ||
                                                !listing.compressed_pictures[desktopPhotoIndex]) {
                                                return getImageUrl(desktopPhotoIndex);
                                            }
                                            const compressedPic = listing.compressed_pictures[desktopPhotoIndex];
                                            return compressedPic.startsWith('http')
                                                ? compressedPic
                                                : `${API_BASE_URL || ''}${compressedPic}`;
                                        })()}
                                        alt={`Listing photo ${desktopPhotoIndex + 1}`}
                                        className="desktop-photo image-base-layer"
                                        loading="eager"
                                        fetchPriority={desktopPhotoIndex === 0 ? 'high' : 'auto'}
                                    />
                                    {/* Full HD image overlay - fades in when loaded with blur transition */}
                                    {getFullImageUrl(desktopPhotoIndex) && (
                                        <img
                                            key={`desktop-full-${desktopPhotoIndex}`}
                                            src={getFullImageUrl(desktopPhotoIndex)}
                                            alt={`Listing photo ${desktopPhotoIndex + 1}`}
                                            className="desktop-photo image-hd-layer"
                                            loading="eager"
                                            // @ts-ignore
                                            fetchpriority={desktopPhotoIndex === 0 ? 'high' : 'auto'}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                opacity: loadedFullImages.has(desktopPhotoIndex) ? 1 : 0,
                                                filter: loadedFullImages.has(desktopPhotoIndex) ? 'blur(0px)' : 'blur(10px)',
                                                transition: 'opacity 0.3s ease-in-out, filter 0.3s ease-in-out',
                                                zIndex: 2,
                                                pointerEvents: 'none',
                                            }}
                                            onLoad={() => {
                                                if (!loadedFullImages.has(desktopPhotoIndex)) {
                                                    requestAnimationFrame(() => {
                                                        setLoadedFullImages(prev => new Set(prev).add(desktopPhotoIndex));
                                                    });
                                                }
                                            }}
                                            onError={() => {
                                                // If HD image fails to load, just skip it - compressed version will remain visible
                                                console.warn(`HD image ${desktopPhotoIndex + 1} failed to load, using compressed version`);
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Desktop navigation arrows */}
                                {listing.pictures && listing.pictures.length > 1 && (
                                    <>
                                        <button 
                                            className="desktop-nav desktop-nav-prev" 
                                            onClick={(e) => { e.stopPropagation(); prevViewFullImage(); }}
                                            aria-label="Previous photo"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="15 18 9 12 15 6"></polyline>
                                            </svg>
                                        </button>
                                        <button 
                                            className="desktop-nav desktop-nav-next" 
                                            onClick={(e) => { e.stopPropagation(); nextViewFullImage(); }}
                                            aria-label="Next photo"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="9 18 15 12 9 6"></polyline>
                                            </svg>
                                        </button>
                                    </>
                                )}

                                {/* Desktop photo counter */}
                                {listing.pictures && listing.pictures.length > 0 && (
                                    <div className="desktop-photo-counter">
                                        {desktopPhotoIndex + 1} / {listing.pictures.length}
                                    </div>
                                )}
                            </div>

                            {/* Desktop dot indicators removed */}
                        </div>

                        {/* Mobile layout - animated carousel */}
                        <div className="mobile-photos" style={{ position: 'relative' }}>
                            {listing.pictures && listing.pictures.length > 0 ? (
                                <ImageCarousel
                                    images={listing.pictures
                                        .map((_, idx) => getImageUrl(idx))
                                        .filter((url): url is string => url !== undefined && url !== null && url.trim() !== '')}
                                    hdImages={listing.pictures
                                        .map((_, idx) => getFullImageUrl(idx))
                                        .filter((url): url is string => url !== undefined && url !== null && url.trim() !== '')}
                                    alt="Listing photos"
                                    className="mobile-photo-carousel"
                                    onImageClick={() => openGallery(mobilePhotoIndex)}
                                    onIndexChange={(index) => setMobilePhotoIndex(index)}
                                    fetchPriority="high"
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* Desktop thumbnail strip */}
                    {listing.pictures && listing.pictures.length > 0 && (
                        <div className="thumbnail-strip">
                            <button
                                type="button"
                                className="thumb-nav thumb-nav-prev"
                                onClick={() => scrollThumbnails('left')}
                                aria-label="Scroll thumbnails left"
                            >
                                ‹
                            </button>
                            <div className="thumbnail-scroll-wrapper">
                                <div className="thumbnail-scroll" ref={thumbnailsRef} role="listbox" aria-label="Listing photos">
                                    {listing.pictures.map((_, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            role="option"
                                            aria-selected={index === desktopPhotoIndex}
                                            aria-label={`Show photo ${index + 1}`}
                                            className={`thumbnail-item ${index === desktopPhotoIndex ? 'active' : ''}`}
                                            data-thumb-index={index}
                                            onClick={() => setDesktopPhotoIndex(index)}
                                        >
                                            <div className="image-layer-container thumbnail-layer">
                                                {/* Compressed thumbnail as base */}
                                                <ImageWithFallback
                                                    src={(() => {
                                                        if (!listing?.compressed_pictures ||
                                                            !listing.compressed_pictures[index]) {
                                                            return getImageUrl(index);
                                                        }
                                                        const compressedPic = listing.compressed_pictures[index];
                                                        return compressedPic.startsWith('http')
                                                            ? compressedPic
                                                            : `${API_BASE_URL || ''}${compressedPic}`;
                                                    })()}
                                                    alt={`Thumbnail ${index + 1}`}
                                                    className="thumbnail-image image-base-layer"
                                                    loading={index === 0 ? 'eager' : 'lazy'}
                                                    onLoad={() => {
                                                        // Preload full version when thumbnail loads
                                                        if (!loadedFullImages.has(index)) {
                                                            const fullImageUrl = getFullImageUrl(index);
                                                            if (fullImageUrl) {
                                                                const img = new Image();
                                                                img.onload = () => {
                                                                    setLoadedFullImages(prev => new Set(prev).add(index));
                                                                };
                                                                img.src = fullImageUrl;
                                                            }
                                                        }
                                                    }}
                                                />
                                                {/* Full HD thumbnail overlay - fades in when loaded with blur transition */}
                                                {getFullImageUrl(index) && (
                                                    <img
                                                        src={getFullImageUrl(index)}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        className="thumbnail-image image-hd-layer"
                                                        loading={index === 0 ? 'eager' : 'lazy'}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            opacity: loadedFullImages.has(index) ? 1 : 0,
                                                            filter: loadedFullImages.has(index) ? 'blur(0px)' : 'blur(10px)',
                                                            transition: 'opacity 0.3s ease-in-out, filter 0.3s ease-in-out',
                                                            zIndex: 2,
                                                            pointerEvents: 'none',
                                                        }}
                                                        onLoad={() => {
                                                            if (!loadedFullImages.has(index)) {
                                                                requestAnimationFrame(() => {
                                                                    setLoadedFullImages(prev => new Set(prev).add(index));
                                                                });
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="thumb-nav thumb-nav-next"
                                onClick={() => scrollThumbnails('right')}
                                aria-label="Scroll thumbnails right"
                            >
                                ›
                            </button>
                        </div>
                    )}

                    {/* Description - always visible, fades for unauth users */}
                    {listing.description && (
                        <div className={`description-card ${!isAuthenticated ? 'description-card-unauth' : ''}`}>
                            <h3>Description</h3>
                            <div className={!isAuthenticated ? 'description-fade-container' : ''}>
                                <p className="description-text">
                                    {listing.description}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Auth Wall Container - covers property details and everything after description */}
                    <div className={`auth-wall-container ${!isAuthenticated ? 'unauthorized' : ''}`}>
                        {!isAuthenticated && (
                            <div className="auth-wall-overlay">
                                <div className="auth-wall-content">
                                    <h3 className="auth-wall-title">Want to see more?</h3>
                                    <p className="auth-wall-text">Sign in or create an account to view full details and contact the owner.</p>
                                    <button 
                                        className="auth-wall-btn"
                                        onClick={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            setTimeout(() => setIsSignInPopupOpen(true), 500);
                                        }}
                                    >
                                        Sign in / Create Account
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={!isAuthenticated ? 'auth-wall-blurred' : ''}>
                            {/* Property Details (with Pricing) & Right Column (Tags + Contact) - 50/50 split */}
                            <div className="details-pricing-container">
                        {/* Property Details + Pricing */}
                        <div className="detail-card">
                            <h3>Property Details</h3>
                            <div className="detail-item">
                                <strong>Monthly Rent:</strong> ${listing.monthly_rent}
                            </div>
                            <div className="detail-item">
                                <strong>Address:</strong> {listing.address}
                            </div>
                            {listing.city && (
                                <div className="detail-item">
                                    <strong>City:</strong> {listing.city}
                                </div>
                            )}
                            <div className="detail-item">
                                <strong>Unit Type:</strong> {formatUnitType(listing.unit_type)}
                            </div>
                            {listing.university_name && listing.university_name !== 'General' && listing.university_id !== 0 && (
                                <div className="detail-item">
                                    <strong>University:</strong> {listing.university_name}
                                </div>
                            )}
                        </div>
                        {/* Right column split: Tags (top) + Contact (bottom) */}
                        <div className="right-split-column">
                            <div className="detail-card">
                                <h3>Tags</h3>
                                <div className="amenities-capsules">
                                    {/* Gender Preferences */}
                                    {listing.gender_preference === 'male_preferred' && (
                                        <span className="feature-capsule gender-male">
                                            Males
                                        </span>
                                    )}
                                    {listing.gender_preference === 'female_preferred' && (
                                        <span className="feature-capsule gender-female">
                                            Females
                                        </span>
                                    )}

                                    {/* Laundry */}
                                    {(listing.laundry === true || String(listing.laundry).toLowerCase() === "true") && (
                                        <span className="feature-capsule">
                                            Laundry
                                        </span>
                                    )}

                                    {/* Parking */}
                                    {(listing.parking === true || String(listing.parking).toLowerCase() === "true") && (
                                        <span className="feature-capsule parking">
                                            Parking
                                        </span>
                                    )}

                                    {/* Pet Friendly */}
                                    {(listing.allow_pets === true || String(listing.allow_pets).toLowerCase() === "true") && (
                                        <span className="feature-capsule pets">
                                            Pet Friendly
                                        </span>
                                    )}

                                    {/* Gym */}
                                    {(listing.gym === true || String(listing.gym).toLowerCase() === "true") && (
                                        <span className="feature-capsule gym">
                                            Gym
                                        </span>
                                    )}

                                    {/* Pool */}
                                    {(listing.pool === true || String(listing.pool).toLowerCase() === "true") && (
                                        <span className="feature-capsule pool">
                                            Pool
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="detail-card">
                                <h3>Contact</h3>
                                <div className="contact-info">
                                    {/* Alt-listings or Facebook listings with Facebook user info */}
                                    {(listing.is_alt_listing || listing.source === 'facebook') && (listing.facebook_first_name || listing.facebook_last_name) ? (
                                        <>
                                            <div className="owner-info">
                                                <div className="owner-avatar">
                                                    {listing.facebook_avatar_url ? (
                                                        <img
                                                            src={listing.facebook_avatar_url}
                                                            alt={`${listing.facebook_first_name || ''} ${listing.facebook_last_name || ''}`.trim() || 'User'}
                                                            className="owner-avatar-img"
                                                            onError={(e) => {
                                                                // Hide image and show placeholder on error
                                                                e.currentTarget.style.display = 'none';
                                                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                                                if (placeholder) placeholder.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className="owner-avatar-placeholder"
                                                        style={{
                                                            display: listing.facebook_avatar_url ? 'none' : 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <circle cx="12" cy="12" r="12" fill="#E8F5E9" />
                                                            <circle cx="12" cy="9" r="3.5" fill="#81C784" />
                                                            <path
                                                                d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
                                                                fill="#81C784"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div className="owner-details">
                                                    <h4 className="owner-name">
                                                        {`${listing.facebook_first_name || ''} ${listing.facebook_last_name || ''}`.trim() || 'Facebook User'}
                                                    </h4>
                                                </div>
                                            </div>
                                        </>
                                    ) : listing.is_alt_listing ? (
                                        /* Alt-listing without Facebook user info - show placeholder */
                                        <div className="owner-info">
                                            <div className="owner-avatar">
                                                <div
                                                    className="owner-avatar-placeholder"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <circle cx="12" cy="12" r="12" fill="#E8F5E9" />
                                                        <circle cx="12" cy="9" r="3.5" fill="#81C784" />
                                                        <path
                                                            d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
                                                            fill="#81C784"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="owner-details">
                                                <h4 className="owner-name">Facebook User</h4>
                                            </div>
                                        </div>
                                    ) : owner ? (
                                        <div className="owner-info">
                                            <div className="owner-avatar-wrapper" style={{ position: 'relative' }}>
                                                <div className="owner-avatar">
                                                    {owner.avatar_url ? (
                                                        <img
                                                            src={owner.avatar_url}
                                                            alt={`${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'User'}
                                                            className="owner-avatar-img"
                                                            onError={(e) => {
                                                                // Hide image and show placeholder on error
                                                                e.currentTarget.style.display = 'none';
                                                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                                                if (placeholder) placeholder.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className="owner-avatar-placeholder"
                                                        style={{
                                                            display: owner.avatar_url ? 'none' : 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <circle cx="12" cy="12" r="12" fill="#E8F5E9" />
                                                            <circle cx="12" cy="9" r="3.5" fill="#81C784" />
                                                            <path
                                                                d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
                                                                fill="#81C784"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="owner-details">
                                                <h4 className="owner-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                    {`${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'Unknown User'}
                                                    {owner.student_verification_status === 'approved' ? (
                                                        <span title="Verified User" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="12" cy="12" r="10" fill="#10b981" />
                                                                <path d="M8.5 12.5L11 15L16 9.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <span className="owner-unverified-icon" aria-label="unverified">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <circle cx="12" cy="12" r="10" fill="#EF4444" />
                                                                <path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                                                            </svg>
                                                            <span className="owner-unverified-tooltip" role="tooltip">unverified</span>
                                                        </span>
                                                    )}
                                                </h4>
                                                {owner.university && (
                                                    <div className="owner-university">{owner.university}</div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <p>Loading owner information...</p>
                                    )}

                                    {messageError && (
                                        <div className="error-message">
                                            ❌ {messageError}
                                        </div>
                                    )}

                                    {/* Show contact buttons if user is not the owner, OR if it's an alt listing (always show Facebook button for alt listings) */}
                                    {(user?.id !== listing?.user_id || listing.is_alt_listing || (!listing.user_id && listing.facebook_url)) && (
                                        <div className="contact-buttons">
                                            {/* Show link button for alt-listings - always show if it's an alt listing and has facebook_url */}
                                            {(listing.is_alt_listing || (!listing.user_id && listing.facebook_url)) && listing.facebook_url ? (
                                                <a
                                                    href={listing.facebook_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="contact-btn primary"
                                                >
                                                    <svg className="link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                    </svg>
                                                    View on Facebook
                                                </a>
                                            ) : (listing.source === 'facebook' && listing.facebook_url && listing.user_id === 'user_1765811664056') ? (
                                                /* Unclaimed Facebook listings */
                                                <a
                                                    href={listing.facebook_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="contact-btn primary"
                                                >
                                                    <svg className="link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                    </svg>
                                                    View on Facebook
                                                </a>
                                            ) : (
                                                <>
                                                    {/* Show message button if listing has a user_id (claimed or regular listing) */}
                                                    <div className={`contact-action-row ${(listing.instagram_username || owner?.instagram) ? 'has-instagram' : ''}`}>
                                                        {isAuthenticated ? (
                                                            <button
                                                                className="contact-btn primary"
                                                                onClick={handleSendMessage}
                                                                disabled={!listing?.user_id}
                                                            >
                                                                Message
                                                                <svg className="send-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <line x1="22" y1="2" x2="11" y2="13" />
                                                                    <polygon points="22,2 15,22 11,13 2,9 22,2" />
                                                                </svg>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="contact-btn primary"
                                                                onClick={() => setShowLockedPopup(true)}
                                                            >
                                                                Message
                                                                <svg className="lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        {(listing.instagram_username || owner?.instagram) && (
                                                            <a
                                                                href={`https://instagram.com/${listing.instagram_username || owner?.instagram}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="instagram-contact-btn"
                                                                aria-label="Visit Instagram Profile"
                                                            >
                                                                <img src={instagramIcon} alt="Instagram" />
                                                            </a>
                                                        )}
                                                    </div>
                                                    {/* Show Facebook link button for regular listings with facebook_url */}
                                                    {listing.facebook_url && (
                                                        <a
                                                            href={listing.facebook_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="contact-btn primary"
                                                        >
                                                            <svg className="link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                            </svg>
                                                            View on Facebook
                                                        </a>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

            {/* Desktop Full Screen View */}
            {viewFull && listing?.pictures && (
                <div
                    className="view-full-overlay"
                    onClick={closeViewFull}
                >
                    <div className="view-full-container" onClick={(e) => e.stopPropagation()}>
                        <div className="view-full-image-wrapper">
                            {listing.pictures && listing.pictures[desktopPhotoIndex] ? (
                                <img
                                    src={getFullImageUrl(desktopPhotoIndex) || getImageUrl(desktopPhotoIndex)}
                                    alt={`Full view image ${desktopPhotoIndex + 1}`}
                                    className="view-full-image"
                                />
                            ) : (
                                <div className="view-full-no-image">
                                    No image available
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Left navigation button - positioned relative to overlay */}
                    {listing.pictures.length > 1 && (
                        <button
                            className="view-full-nav view-full-nav-left"
                            onClick={(e) => { e.stopPropagation(); prevViewFullImage(); }}
                            aria-label="Previous image"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                        </button>
                    )}
                    {/* Right navigation button - positioned relative to overlay */}
                    {listing.pictures.length > 1 && (
                        <button
                            className="view-full-nav view-full-nav-right"
                            onClick={(e) => { e.stopPropagation(); nextViewFullImage(); }}
                            aria-label="Next image"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {/* Gallery Popup */}
            {galleryOpen && listing?.pictures && (
                <div
                    className="gallery-overlay"
                    onClick={closeGallery}
                >
                    <div className="gallery-popup" onClick={(e) => e.stopPropagation()}>
                        <button className="gallery-close" onClick={closeGallery}>
                            ×
                        </button>
                        <div className="gallery-image-container gallery-image-container-mobile">
                            {listing.pictures && listing.pictures[currentImageIndex] ? (
                                <ZoomableImage key={currentImageIndex}>
                                    <div className="image-layer-container">
                                        {/* Always show HD image for gallery */}
                                        <ImageWithFallback
                                            key={`gallery-hd-${currentImageIndex}`}
                                            src={getFullImageUrl(currentImageIndex) || getImageUrl(currentImageIndex)}
                                            alt={`Gallery image ${currentImageIndex + 1}`}
                                            className="gallery-image"
                                        />
                                    </div>
                                </ZoomableImage>
                            ) : (
                                <div className="gallery-no-image">
                                    No image available
                                </div>
                            )}
                        </div>
                        <div className="gallery-controls-mobile">
                            <button
                                className="gallery-nav-btn-mobile prev"
                                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                            </button>
                            <div className="gallery-counter">
                                {currentImageIndex + 1} / {listing.pictures.length}
                            </div>
                            <button
                                className="gallery-nav-btn-mobile next"
                                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <GlassCapsulePopup
                message="Create an account or sign in to message others."
                show={showLockedPopup}
                onClose={() => setShowLockedPopup(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message="Link copied to clipboard."
                show={showSharePopup}
                onClose={() => setShowSharePopup(false)}
                autoHideDuration={2000}
            />

            <GlassCapsulePopup
                message="Boost Applied!"
                className="fire-popup"
                show={showBoostSuccess}
                onClose={() => setShowBoostSuccess(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message={listing.boost_expiration ? formatTimeRemaining(listing.boost_expiration) : ""}
                className="fire-popup"
                show={showTimeRemaining}
                onClose={() => setShowTimeRemaining(false)}
                autoHideDuration={5000}
            />

            <SignInPopup
                isOpen={isSignInPopupOpen}
                onClose={() => setIsSignInPopupOpen(false)}
                redirectPath={window.location.pathname}
            />

            <BoostModal
                isOpen={isBoostModalOpen}
                onClose={() => setIsBoostModalOpen(false)}
                initialListingId={listing.id}
            />

            <Footer />
                </div>
            </div>
        </>
    );
};

export default Listing;
